import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {PickemWidgetType, RequestState, RoundStatus, TournamentStatus} from "data/enums";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {ITournament} from "data/providers/json/json.provider";
import {Empty} from "data/types/generics";
import {IPrediction} from "data/providers/api/predictions.api.provider";
import {findIndex, get} from "lodash";
import React from "react";
import type {ISquad} from "data/types/api";
import type {ISquadsStore} from "data/stores/squads/squds.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IUserStore} from "data/stores/user/user.store";
import {createFanHubEvent} from "data/utils/helpers";

export enum CtaType {
	USA = "United States",
	CANADA = "Canada",
	Default = "default",
}

const CtaMap: Record<string, CtaType> = {
	[CtaType.USA]: CtaType.USA,
	[CtaType.CANADA]: CtaType.CANADA,
};

interface IProps {
	tournament: ITournament;
}

export interface IPickItemController extends ViewController<IProps> {
	get prediction(): Empty<IPrediction>;

	get isLockout(): boolean;

	get isComplete(): boolean;

	get isLive(): boolean;

	get tip(): {
		[key: string]: number;
	} | null;

	get isSelectedHome(): boolean;

	get isSelectedAway(): boolean;

	get homePercent(): number;

	get awayPercent(): number;

	get homeSquad(): ISquad | undefined;

	get awaySquad(): ISquad | undefined;

	get percentForRoundProgress(): number;

	get homeScore(): number | null;

	get awayScore(): number | null;

	get isHiddenSelectButton(): boolean;

	get isNeedMargin(): boolean;

	get isNeedToShowStats(): boolean;

	get tuneCtaType(): CtaType | null;

	get cardHeadCn(): string;

	margin: number;
	isOpenStats: boolean;
	requestState: RequestState;
	toggleStats: () => void;
	onPick: (squadId: number) => void;
	updateMargin: (nevMargin: number) => void;
	onSettMargin: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

@injectable()
export class PickItemController implements IPickItemController {
	@observable requestState: RequestState = RequestState.IDLE;
	@observable margin: number = 0;
	@observable isOpenStats: boolean = false;

	@observable private _tournament: ITournament | undefined;
	@observable private _isNeedMargin: boolean = false;

	constructor(
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	get tournamentId() {
		return this._tournament?.id || 0;
	}

	get prediction() {
		return this._predictionsStore.getPredictionByTournamentId(this.tournamentId);
	}

	get tip() {
		return get(this._predictionsStore.tips, this.tournamentId, null);
	}

	get isLockout(): boolean {
		return this._tournament?.status !== TournamentStatus.Scheduled;
	}

	get isComplete(): boolean {
		return this._tournament?.status === TournamentStatus.Complete;
	}

	get isLive(): boolean {
		return this._tournament?.status === TournamentStatus.Active;
	}

	get isSelectedHome(): boolean {
		return this.prediction?.squad === this._tournament?.homeSquadId;
	}

	get isSelectedAway(): boolean {
		return this.prediction?.squad === this._tournament?.awaySquadId;
	}

	get homePercent(): number {
		const homeSquadId = get(this._tournament, "homeSquadId", 0);
		const value = get(this.tip, homeSquadId, 0).toFixed();
		return parseInt(value);
	}

	get awayPercent(): number {
		const awaySquadId = get(this._tournament, "awaySquadId", 0);
		const value = get(this.tip, awaySquadId, 0).toFixed();
		return parseInt(value);
	}

	get homeSquad() {
		return this._squadsStore.getSquadById(get(this._tournament, "homeSquadId", 0));
	}

	get awaySquad() {
		return this._squadsStore.getSquadById(get(this._tournament, "awaySquadId", 0));
	}

	get percentForRoundProgress(): number {
		return this.homePercent === 0 && this.awayPercent === 0 ? 50 : this.homePercent;
	}

	get homeScore(): number | null {
		return get(this._tournament, "homeScore", null);
	}

	get awayScore(): number | null {
		return get(this._tournament, "awayScore", null);
	}

	get isHiddenSelectButton(): boolean {
		return this.isLockout && !this.prediction;
	}

	get isNeedMargin(): boolean {
		return this._isNeedMargin;
	}

	get isNeedToShowStats(): boolean {
		const allRounds = this._roundsStore.roundsForPicks;
		const activeRoundIndex = findIndex(
			allRounds,
			(round) => round.id === this._roundsStore.currentSelectedRound?.id
		);
		const isActiveRoundLastInList = activeRoundIndex + 1 === allRounds.length;
		return (
			this._roundsStore.currentSelectedRound?.status !== RoundStatus.Complete ||
			isActiveRoundLastInList
		);
	}

	get tuneCtaType() {
		const userCountry = this._userStore.userCountry;

		return CtaMap[userCountry] ?? CtaType.Default;
	}

	get cardHeadCn() {
		return this.tuneCtaType ? "with-cta" : "";
	}

	dispose(): void {
		return;
	}

	@action
	public toggleStats = (): void => {
		this.isOpenStats = !this.isOpenStats;
	};

	@action
	public onPick = (squadId: number): void => {
		if (this.isLockout) {
			return;
		}

		const data: IPrediction = {
			squad: squadId,
			tournament: this.tournamentId,
		};

		if (this._isNeedMargin) {
			data.margin = this.margin;
		}

		this._predictionsStore.updatePredictions(data);

		createFanHubEvent("c5ff0389-0daf-44f1-91cb-465c429dad8b");
	};

	@action
	public updateMargin = (newMargin: number): void => {
		this.margin = newMargin >= 100 ? 100 : newMargin;

		if (this.prediction) {
			this._predictionsStore.updatePredictions({
				...this.prediction,
				margin: this.margin,
			});
		}
	};

	@action
	public onSettMargin = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
		const newMargin = parseInt(event.currentTarget.value, 0);
		this.updateMargin(newMargin);
	};

	init({tournament}: IProps): void {
		this._tournament = tournament;
		this._isNeedMargin = tournament.isMarginGame;
		this.margin = this.prediction?.margin || 0;

		const queryParams = new URLSearchParams(window.location.search);
		const tournamentId = Number(queryParams.get("tournamentId"));
		const type = queryParams.get("type");

		if (tournament.id === tournamentId && type === PickemWidgetType.Stats) {
			this.isOpenStats = true;
			const element = document.querySelector(`[data-id="pick-card-${tournament.id}"]`);
			element?.scrollIntoView();
		}
	}
}
